import React, { useMemo, useState } from 'react';
import { Pressable, View } from 'react-native';
import {
  customersFromOrders,
  fmt,
  formatClockTime,
  formatDate,
  searchCustomers,
  useAppStore,
} from '@restorant/core';
import type { CustomerRecord } from '@restorant/core';
import { radius, useColors } from '../theme';
import { useT, useTableLabel } from '../i18n';
import { Icon } from '../icons/Icon';
import { Screen } from '../components/Screen';
import { EmptyNote } from '../components/EmptyNote';
import { Panel } from '../components/Panel';
import { SectionTitle } from '../components/SectionTitle';
import { StatTile } from '../components/StatTile';
import { T } from '../components/Text';
import { TextField } from '../components/TextField';

function Stat({ label, value }: { label: string; value: string }) {
  const colors = useColors();
  return (
    <View style={{ gap: 2, flexGrow: 1, flexBasis: 110 }}>
      <T size={10.5} weight={700} color={colors.textFaint} style={{ letterSpacing: 0.4 }}>
        {label.toUpperCase()}
      </T>
      <T size={13} weight={600}>
        {value}
      </T>
    </View>
  );
}

/** One customer, expanding to show every order they placed and when. */
function CustomerCard({ customer }: { customer: CustomerRecord }) {
  const colors = useColors();
  const t = useT();
  const tableLabel = useTableLabel();
  const openOrderDetail = useAppStore((s) => s.openOrderDetail);
  const [open, setOpen] = useState(false);

  const visits =
    customer.visits === 1 ? t('visit_1') : t('visits', { n: customer.visits });

  return (
    <View
      style={{
        borderWidth: 1,
        borderColor: colors.line,
        borderRadius: radius.lg,
        backgroundColor: colors.surface,
        marginBottom: 10,
        overflow: 'hidden',
      }}
    >
      <Pressable
        onPress={() => setOpen((v) => !v)}
        testID={`customer-${customer.key}`}
        style={({ hovered }: any) => ({
          flexDirection: 'row',
          alignItems: 'center',
          gap: 12,
          padding: 14,
          backgroundColor: hovered ? colors.surface2 : 'transparent',
        })}
      >
        <Icon name="user" size={18} color={colors.gold} />
        <View style={{ flex: 1, gap: 2, minWidth: 0 }}>
          <T size={14} weight={700} numberOfLines={1}>
            {customer.name || customer.phone}
          </T>
          <T size={11.5} color={colors.textFaint} numberOfLines={1}>
            {[customer.name ? customer.phone : null, visits].filter(Boolean).join(' · ')}
          </T>
        </View>
        <View style={{ alignItems: 'flex-end', gap: 2 }}>
          <T size={14} weight={700} color={colors.gold}>
            {fmt(customer.totalSpent)}
          </T>
          <T size={11} color={colors.textFaint}>
            {formatDate(new Date(customer.lastAt))}
          </T>
        </View>
        <Icon name={open ? 'chevronDown' : 'chevronRight'} size={16} color={colors.textFaint} />
      </Pressable>

      {open ? (
        <View style={{ padding: 14, gap: 12, borderTopWidth: 1, borderTopColor: colors.line }}>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
            <Stat label={t('first_visit')} value={formatDate(new Date(customer.firstAt))} />
            <Stat label={t('last_visit')} value={formatDate(new Date(customer.lastAt))} />
            <Stat label={t('total_spent')} value={fmt(customer.totalSpent)} />
          </View>

          <T size={11.5} weight={700} color={colors.textFaint} style={{ letterSpacing: 0.4 }}>
            {t('customer_orders').toUpperCase()}
          </T>

          {customer.orders.map((o) => (
            <Pressable
              key={o.id}
              onPress={() => openOrderDetail(o.id)}
              style={({ hovered }: any) => ({
                flexDirection: 'row',
                alignItems: 'center',
                gap: 10,
                paddingVertical: 9,
                paddingHorizontal: 11,
                borderRadius: radius.md,
                borderWidth: 1,
                borderColor: hovered ? colors.goldDim : colors.line,
                backgroundColor: colors.surface2,
              })}
            >
              <Icon name="clock" size={14} color={colors.textFaint} />
              <View style={{ flex: 1, gap: 2, minWidth: 0 }}>
                <T size={12.5} weight={600} numberOfLines={1}>
                  {t('order_no', { n: o.orderNo ?? o.localNo })} · {tableLabel(o.tableId)}
                </T>
                {/* The day they came, which is the question this screen answers. */}
                <T size={11} color={colors.textFaint}>
                  {formatDate(new Date(o.placedAt))} · {formatClockTime(new Date(o.placedAt))}
                </T>
              </View>
              <T size={12.5} weight={700} color={colors.gold}>
                {fmt(o.total)}
              </T>
            </Pressable>
          ))}
        </View>
      ) : null}
    </View>
  );
}

/**
 * Everyone who left a name or a phone number on an order.
 *
 * Derived from the order history rather than stored separately, so it is
 * automatically right: an order pulled from another device, or amended later,
 * updates the customer with it.
 */
export function CustomersScreen({ contentPadding }: { contentPadding: number }) {
  // Hoisted, not called inside the JSX below: a hook in a conditional branch
  // changes the hook count between renders and React rejects it.
  const colors = useColors();
  const t = useT();
  const orders = useAppStore((s) => s.orders);
  const [query, setQuery] = useState('');

  const all = useMemo(() => customersFromOrders(orders), [orders]);
  const shown = useMemo(() => searchCustomers(all, query), [all, query]);

  const repeat = all.filter((c) => c.visits > 1).length;
  const spend = all.reduce((s, c) => s + c.totalSpent, 0);

  return (
    <Screen contentPadding={contentPadding}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 14 }}>
        <StatTile value={String(all.length)} label={t('title_customers')} icon="user" />
        <StatTile value={String(repeat)} label={t('visits', { n: 2 })} icon="clock" />
        <StatTile value={fmt(spend)} label={t('total_spent')} icon="rupee" />
      </View>

      <SectionTitle title={t('title_customers')} icon="user" />

      {all.length ? (
        <>
          <View style={{ marginBottom: 14 }}>
            <TextField
              testID="customer-search"
              label={t('search_customers')}
              icon="search"
              value={query}
              onChangeText={setQuery}
              placeholder={t('search_customers')}
            />
          </View>

          {shown.length ? (
            shown.map((c) => <CustomerCard key={c.key} customer={c} />)
          ) : (
            <EmptyNote>{t('no_match')}</EmptyNote>
          )}
        </>
      ) : (
        <Panel>
          <EmptyNote>{t('no_customers')}</EmptyNote>
          <T
            size={12}
            style={{ textAlign: 'center', paddingBottom: 12 }}
            color={colors.textFaint}
          >
            {t('no_customers_hint')}
          </T>
        </Panel>
      )}
    </Screen>
  );
}
